/** Public tenant website (Phase 0) — platform-admin shapes. */

export interface TenantSite {
  tenant_id: string;
  slug: string;
  /** Derived server-side from PUBLIC_SITE_DOMAIN; never stored. */
  live_url: string;
  primary_location_id: string;
  primary_location_name: string | null;
  primary_location_active: boolean;
  is_enabled: boolean;
  allow_indexing: boolean;
  hide_prices: boolean;
  show_stock_status: boolean;
  enabled_at: string | null;
  enabled_by_name: string | null;
  slug_changed_at: string | null;
  notes: string | null;
  /** Readiness signals — advisory input to the "Allow indexing" decision. */
  public_product_count: number;
  products_with_images: number;
  seo_override_count: number;
  has_address: boolean;
  has_phone: boolean;
}

export interface SitePageSeo {
  page_key: string;
  meta_title: string | null;
  meta_description: string | null;
  noindex: boolean;
}

export type SlugProblem = 'FORMAT' | 'LENGTH' | 'RESERVED' | 'PUNYCODE' | 'TAKEN';

export interface SlugAvailability {
  slug: string;
  available: boolean;
  reason: SlugProblem | null;
  suggestions: string[];
}

/** Pages an admin can override SEO for. Entity pages use `product:<uuid>`. */
export const SITE_PAGE_KEYS = ['home', 'catalogue', 'store', 'contact'] as const;

export const SITE_PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  catalogue: 'Catalogue',
  store: 'Store & contact',
  contact: 'Contact',
};

// ─── Phase 1: custom customer domains ────────────────────────────────────────

export type TenantDomainStatus = 'PENDING' | 'VERIFIED' | 'ACTIVE' | 'FAILED' | 'DISABLED';
export type TenantDomainWwwStatus = 'NONE' | 'SKIPPED' | 'ACTIVE';

export interface TenantDomain {
  tenant_id: string;
  hostname: string;
  is_apex: boolean;
  status: TenantDomainStatus;
  verification_token: string;
  verified_via: 'A' | 'AAAA' | 'CNAME' | 'TXT' | null;
  verified_at: string | null;
  activated_at: string | null;
  www_status: TenantDomainWwwStatus;
  hostname_changed_at: string | null;
  previous_hostname: string | null;
  last_checked_at: string | null;
  last_check_status: 'OK' | 'FAILED' | null;
  last_check_error: string | null;
  cert_expires_at: string | null;
  cert_issuer: string | null;
  http_last_status: number | null;
  consecutive_failures: number;
  notes: string | null;
  created_at: string;
  /** Derived server-side; null until the domain is servable. */
  live_url: string | null;
  txt_record: { name: string; value: string };
  dns_instructions: string[];
  /** Present on the response to a change/park, never on a plain read. */
  previous_hostname_freed?: string | null;
  teardown_required?: boolean;
  teardown_command?: string | null;
}

/** Outcome of "Verify now". Never an error — unpropagated DNS is normal. */
export interface DomainVerifyResult {
  ok: boolean;
  status: TenantDomainStatus;
  verified_via?: 'A' | 'AAAA' | 'CNAME' | 'TXT';
  reason?: 'NOT_FOUND' | 'NO_MATCH' | 'SERVER_IP_NOT_CONFIGURED' | 'TIMEOUT' | 'ERROR';
  detail?: string;
  next_step?: string | null;
  observed: { a: string[]; aaaa: string[]; cname: string[]; txt: string[] };
}

export const DOMAIN_STATUS_LABELS: Record<TenantDomainStatus, string> = {
  PENDING: 'Awaiting DNS',
  VERIFIED: 'DNS verified — certificate pending',
  ACTIVE: 'Live',
  FAILED: 'Verification failed',
  DISABLED: 'Parked',
};
